import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { showToastMessage } from '../../../../components/Toast'
import {
  getAttendanceSettings,
  getBiometricStatus,
  saveAttendanceSettings,
} from '../api/hrmsAttendanceSettings.api'
import { attendanceSettingsSchema } from '../schemas/attendanceSettings.schema'
import { getApiErrorMessage } from '../../../../shared/utils/errorUtils'
import type {
  AttendanceSettingsFormValues,
  HrmsAttendanceSettingsDto,
  HrmsAttendanceSettingsUpdatePayload,
} from '../types/hrmsAttendanceSettings.types'
import { downloadAttendanceTemplate } from '../utils/downloadAttendanceTemplate'

export const HRMS_ATTENDANCE_SETTINGS_QUERY_KEY = ['hrms', 'attendance', 'settings'] as const
export const HRMS_BIOMETRIC_STATUS_QUERY_KEY = ['hrms', 'attendance', 'settings', 'biometric-status'] as const

/**
 * Normalizes backend time "HH:mm:ss" to form time "HH:mm".
 */
export const apiTimeToForm = (time?: string): string => {
  if (!time) return '09:00'
  const parts = time.split(':')
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`
  }
  return time
}

/**
 * Normalizes form time "HH:mm" to backend time "HH:mm:ss".
 */
export const formTimeToApi = (time?: string): string => {
  if (!time) return '09:00:00'
  const parts = time.split(':')
  if (parts.length === 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`
  }
  if (parts.length >= 3) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}`
  }
  return time
}

/**
 * Maps API DTO into form values.
 * Note: Half-day API minutes are converted to form hours (mins / 60).
 */
export const mapDtoToFormValues = (dto?: HrmsAttendanceSettingsDto | null): AttendanceSettingsFormValues => {
  const halfDayMinutes = dto?.halfDayThresholdMins ?? 240
  const halfDayHours = Number((halfDayMinutes / 60).toFixed(2))

  return {
    gracePeriodMinutes: dto?.arrivalGracePeriodMins ?? 15,
    lateThresholdMinutes: dto?.lateArrivalThresholdMins ?? 30,
    halfDayHours,
    dailyWorkingHours: dto?.dailyWorkingHours ?? 8,
    absentCutoff: apiTimeToForm(dto?.defaultAbsentTime ?? '19:00:00'),
    shiftMode: dto?.shiftMode?.displayName ?? 'Fixed Academic Hours',
    shiftModeId: dto?.shiftModeId ?? undefined,
    workStart: apiTimeToForm(dto?.workStartTime ?? '09:00:00'),
    workEnd: apiTimeToForm(dto?.workEndTime ?? '17:00:00'),
    biometricEnabled: dto?.biometricEnabled ?? true,
    manualExcelEnabled: dto?.manualExcelEnabled ?? true,
    selfServiceEnabled: true,
    managerApprovalRequired: true,
    adminOverrideEnabled: false,
  }
}

/**
 * Maps form values into PUT payload.
 * Note: Half-day form hours are converted to API minutes (hours * 60).
 * Never sends id, branchId, shiftMode, createdAt, updatedAt.
 */
export const mapFormValuesToPayload = (
  values: AttendanceSettingsFormValues
): HrmsAttendanceSettingsUpdatePayload => {
  return {
    lateArrivalThresholdMins: Number(values.lateThresholdMinutes),
    halfDayThresholdMins: Math.round(Number(values.halfDayHours) * 60),
    defaultAbsentTime: formTimeToApi(values.absentCutoff),
    workStartTime: formTimeToApi(values.workStart),
    workEndTime: formTimeToApi(values.workEnd),
    shiftModeId: values.shiftModeId || undefined,
    dailyWorkingHours: Number(values.dailyWorkingHours),
    arrivalGracePeriodMins: Number(values.gracePeriodMinutes),
    biometricEnabled: Boolean(values.biometricEnabled),
    manualExcelEnabled: Boolean(values.manualExcelEnabled),
  }
}

/**
 * Custom hook providing TanStack React Query hooks and form state
 * for HRMS Attendance Settings.
 */
export function useHrmsAttendanceSettings() {
  const queryClient = useQueryClient()
  const [isReadOnly, setIsReadOnly] = useState(false)

  // Section editing flags for cards
  const [editRules, setEditRules] = useState(false)
  const [editHours, setEditHours] = useState(false)

  const settingsQuery = useQuery({
    queryKey: HRMS_ATTENDANCE_SETTINGS_QUERY_KEY,
    queryFn: getAttendanceSettings,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  })

  const biometricStatusQuery = useQuery({
    queryKey: HRMS_BIOMETRIC_STATUS_QUERY_KEY,
    queryFn: getBiometricStatus,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  })

  const initialFormValues = useMemo(() => {
    return mapDtoToFormValues(settingsQuery.data)
  }, [settingsQuery.data])

  const form = useForm<AttendanceSettingsFormValues>({
    resolver: zodResolver(attendanceSettingsSchema) as any,
    defaultValues: initialFormValues,
    mode: 'onChange',
  })

  const lastSyncedRef = useRef<string | HrmsAttendanceSettingsDto | undefined>(undefined)

  // Synchronize form baseline when server query resolves
  useEffect(() => {
    if (settingsQuery.data) {
      const currentKey = settingsQuery.data.updatedAt ?? settingsQuery.data
      if (lastSyncedRef.current !== currentKey) {
        lastSyncedRef.current = currentKey
        if (!form.formState.isDirty) {
          form.reset(mapDtoToFormValues(settingsQuery.data))
        }
      }
    }
  }, [settingsQuery.data])

  // Handle 403 on initial fetch
  useEffect(() => {
    const err = settingsQuery.error as any
    if (err?.response?.status === 403) {
      setIsReadOnly(true)
      showToastMessage(
        getApiErrorMessage(err, 'You do not have permission to modify attendance settings.'),
        'error'
      )
    }
  }, [settingsQuery.error])

  const updateMutation = useMutation({
    mutationFn: (payload: HrmsAttendanceSettingsUpdatePayload) => saveAttendanceSettings(payload),
    onSuccess: (savedData) => {
      queryClient.setQueryData(HRMS_ATTENDANCE_SETTINGS_QUERY_KEY, savedData)
      queryClient.invalidateQueries({ queryKey: HRMS_BIOMETRIC_STATUS_QUERY_KEY })
      form.reset(mapDtoToFormValues(savedData))
      setEditRules(false)
      setEditHours(false)
      showToastMessage('Attendance settings saved successfully.', 'success')
    },
    onError: (err: any) => {
      if (err?.response?.status === 403) {
        setIsReadOnly(true)
      }
      const errMsg = getApiErrorMessage(err, 'Failed to save attendance settings.')
      showToastMessage(errMsg, 'error')
    },
  })

  const toggleEditRules = useCallback(async () => {
    if (isReadOnly) return
    if (editRules) {
      const valid = await form.trigger([
        'gracePeriodMinutes',
        'lateThresholdMinutes',
        'halfDayHours',
        'absentCutoff',
      ])
      if (valid) setEditRules(false)
    } else {
      setEditRules(true)
    }
  }, [editRules, form, isReadOnly])

  const toggleEditHours = useCallback(async () => {
    if (isReadOnly) return
    if (editHours) {
      const valid = await form.trigger([
        'workStart',
        'workEnd',
        'dailyWorkingHours',
        'halfDayHours',
      ])
      if (valid) setEditHours(false)
    } else {
      setEditHours(true)
    }
  }, [editHours, form, isReadOnly])

  // Reset form to last fetched server values
  const handleReset = useCallback(() => {
    form.reset(mapDtoToFormValues(settingsQuery.data))
    setEditRules(false)
    setEditHours(false)
  }, [form, settingsQuery.data])

  const handleDownloadTemplate = useCallback(() => {
    downloadAttendanceTemplate()
    showToastMessage('Attendance template downloaded successfully.', 'success')
  }, [])

  const handleSubmit = form.handleSubmit(
    async (values) => {
      if (isReadOnly) {
        showToastMessage('Settings are in read-only mode.', 'error')
        return
      }
      const payload = mapFormValuesToPayload(values)
      await updateMutation.mutateAsync(payload)
    },
    (errors) => {
      console.error('[HRMS Attendance Settings] Validation errors on submit:', errors)
      const firstError = Object.values(errors)[0] as any
      const errorMsg = firstError?.message || 'Please resolve validation errors before saving.'
      showToastMessage(errorMsg, 'error')
    }
  )

  const isDirty = form.formState.isDirty
  const hasErrors = Object.keys(form.formState.errors).length > 0
  const isValid = !hasErrors
  const isSubmitting = updateMutation.isPending || form.formState.isSubmitting

  return {
    // Queries
    settingsQuery,
    biometricStatusQuery,
    updateMutation,
    data: settingsQuery.data,
    biometricStatus: biometricStatusQuery.data,
    // Status
    isLoading: settingsQuery.isLoading,
    isError: settingsQuery.isError,
    error: settingsQuery.error,
    refetch: settingsQuery.refetch,
    isReadOnly,
    // Form & UI controls
    form,
    isDirty,
    isValid,
    isSubmitting,
    editRules,
    toggleEditRules,
    editHours,
    toggleEditHours,
    handleReset,
    handleDownloadTemplate,
    handleSubmit,
  }
}
